import { useState, useEffect } from 'react';
import { getComments, addComment, deleteComment } from '../services/commentService';

/**
 * Hook para manejar comentarios de un post
 */
export const useComments = (postId) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Cargar comentarios
  const loadComments = async () => {
    if (!postId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await getComments(postId);
      
      if (result.success) {
        setComments(result.comments);
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Agregar comentario
  const handleAddComment = async (userId, userName, userPhotoURL, text) => {
    try {
      setSubmitting(true);
      const trimmed = (text || '').trim();
      if (!trimmed) return { success: false, error: 'Comentario vacío' };

      // Optimista: insertar comentario temporal al inicio
      const tempId = `temp-${Date.now()}`;
      const tempComment = {
        id: tempId,
        postId,
        userId,
        userName,
        userPhotoURL,
        text: trimmed,
        createdAt: new Date()
      };
      setComments((prev) => [tempComment, ...(prev || [])]);

      const result = await addComment(postId, userId, userName, userPhotoURL, trimmed);
      if (result.success) {
        // Reemplazar id temporal por id real
        setComments((prev) =>
          (prev || []).map((c) => (c.id === tempId ? { ...c, id: result.commentId } : c))
        );
        return { success: true };
      }

      // Revertir si falla
      setComments((prev) => (prev || []).filter((c) => c.id !== tempId));
      return { success: false, error: result.error };
    } catch (err) {
      // Revertir comentario temporal si existe
      setComments((prev) => (prev || []).filter((c) => !String(c.id || '').startsWith('temp-')));
      return { success: false, error: err.message };
    } finally {
      setSubmitting(false);
    }
  };

  // Eliminar comentario
  const handleDeleteComment = async (commentId) => {
    try {
      // Optimista: remover localmente
      const prev = comments;
      setComments((cur) => (cur || []).filter((c) => c.id !== commentId));

      const result = await deleteComment(commentId, postId);
      
      if (result.success) {
        return { success: true };
      }
      // Revertir si falla
      setComments(prev);
      return { success: false, error: result.error };
    } catch (err) {
      // Revertir si falla
      await loadComments().catch(() => {});
      return { success: false, error: err.message };
    }
  };

  // Cargar comentarios al montar
  useEffect(() => {
    loadComments();
  }, [postId]);

  return {
    comments,
    loading,
    error,
    submitting,
    addComment: handleAddComment,
    deleteComment: handleDeleteComment,
    refresh: loadComments
  };
};
